/**
 * MarketBlendLabel: the supplier page's one-line market index name, with the
 * full blend on hover (Lane 1428, Ryan 2026-10-25).
 *
 * The line is "Blended market index, composed of X, Y, Z": the indexes with at
 * least 10% of the blend, never more than three, in short buyer names. The
 * details list every index in the blend with its published name and its weight
 * in the blend, largest first, plus the source line when the page has one.
 *
 * Everything shown comes from the server's `market_composition`
 * (helpers/part_market_index.py `market_composition`), which is built from the
 * blend's own `series_breakdown`: the same weights the Market figure uses. This
 * component orders nothing and computes nothing.
 *
 * Reach: hover peeks the details open; a click or a tap pins them; focus opens
 * them for keyboard users; Escape, a second click or a click outside closes
 * them. The details stay in the page while closed (hidden) and the trigger
 * names them with aria-describedby, so a screen reader reads the full list on
 * focus.
 */
import React, { useEffect, useId, useRef, useState } from 'react';

function InfoGlyph() {
  return (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true"
      focusable="false" style={{ display: 'block' }}>
      <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" />
      <circle cx="8" cy="4.6" r="0.95" fill="currentColor" />
      <rect x="7.15" y="6.6" width="1.7" height="5" rx="0.85" fill="currentColor" />
    </svg>
  );
}

// Every share with one decimal (Ryan, 2026-10-26: "78%" beside "13.2%" read as
// two kinds of number). The server already rounds to one decimal.
const fmtShare = (v) => (v == null ? '--' : `${Number(v).toFixed(1)}%`);

/**
 * Props:
 *   composition: `ppi_blend.market_composition` ({label, label_names, series}).
 *   months:      the window the blend is for (6, 12 or 24).
 *   provenance:  `ppi_blend.ppi_provenance` or undefined (source line).
 */
export default function MarketBlendLabel({ composition, months, provenance }) {
  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(false);
  const wrapRef = useRef(null);
  const closeTimer = useRef(null);
  const detailsId = useId();

  const cancelClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };
  const close = () => {
    cancelClose();
    setOpen(false);
    setPinned(false);
  };
  // A short delay, so the pointer can move from the line into the details.
  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = setTimeout(() => {
      setOpen((o) => (pinned ? o : false));
    }, 140);
  };
  useEffect(() => cancelClose, []);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) close();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
    };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const series = (composition && composition.series) || [];
  if (!composition || !composition.label || series.length === 0) return null;

  return (
    <div
      ref={wrapRef}
      className="sd-mblend"
      data-testid="market-blend-label"
      onMouseEnter={() => { cancelClose(); setOpen(true); }}
      onMouseLeave={scheduleClose}
    >
      <button
        type="button"
        className="sd-mblend-trigger"
        aria-expanded={open}
        aria-describedby={detailsId}
        onClick={() => {
          if (pinned) { close(); } else { setOpen(true); setPinned(true); }
        }}
        onFocus={() => setOpen(true)}
        onBlur={(e) => {
          if (pinned) return;
          if (wrapRef.current && wrapRef.current.contains(e.relatedTarget)) return;
          setOpen(false);
        }}
      >
        <span className="sd-mblend-text">{composition.label}</span>
        <span className="sd-mblend-glyph"><InfoGlyph /></span>
      </button>
      <div id={detailsId} role="tooltip" className="sd-mblend-pop" hidden={!open}
        data-testid="market-blend-details">
        <div className="sd-mblend-h">
          What makes up this blend{months ? ` (${months} months)` : ''}
        </div>
        <div className="sd-mblend-sub">Each index is weighted by your spend on the parts it covers.</div>
        <ul className="sd-mblend-list">
          {series.map((s) => (
            <li key={s.series_id}>
              <span className="nm">{s.full_name}</span>
              <span className="pc">{fmtShare(s.share_pct)}</span>
            </li>
          ))}
        </ul>
        {/* The source only. Ryan (2026-10-26) asked that the selection
            disclosure ("Wingman selected these index series ...") not be said
            here; Lane 1432 (2026-10-27) removed it from every surface. */}
        {provenance?.attribution && (
          <div className="sd-mblend-src">Source: {provenance.attribution}.</div>
        )}
      </div>
    </div>
  );
}
